import { Badge, Container, createTheme, Input, rem, type MantineColorsTuple } from '@mantine/core';
import inputClasses from './inputs.module.css';

// Палитра языка «Бюллетень»: свежий зелёный, лаймовая метка, линии вместо карточек.
// Значения и образцы — docs/design-guidelines.md.

// Primary — «свежий зелёный» языка «Бюллетень»: кнопки, ссылки, активные состояния; белый текст на тоне 7 ≈ 5:1 (AA).
const brand: MantineColorsTuple = [
  '#e9f5e3',
  '#d3e9cd',
  '#a9d3a6',
  '#7dbb85',
  '#55a36a',
  '#3a8f58',
  '#2f8551',
  '#2b7a4b',
  '#23653e',
  '#14261c',
];

// Пшенично-янтарный — единственный тёплый акцент палитры (поле и зерно: brand+lime+wheat).
// Применение ТОЧЕЧНОЕ: «★ Лучшее предложение», пустые состояния, предупреждающие счётчики.
const wheat: MantineColorsTuple = [
  '#fdf6ec',
  '#f7e9d0',
  '#f0d4a4',
  '#ecc077',
  '#e8ae52',
  '#e8a33d',
  '#d18a26',
  '#b1741a',
  '#8f5d13',
  '#6f470c',
];

// ── Семантические цвета выгоды ──
// Применяются только к строке выгоды «Дороже/Дешевле на N» у лучшей цены: выигрыш (gain) или потеря (loss).
// Сама лучшая цена отдельного цвета не имеет: крупное число обычным цветом и зелёная кнопка «Продать»/«Купить»
// («Бюллетень», 2026-11-03; прежний оранжевый ask выведен). См. design-guidelines, решение №5.

// Зелёный — ВЫИГРЫШ в дельте относительно моей цены («Дешевле/Дороже» в мою пользу). Опорный gain.6 #16a34a.
const gain: MantineColorsTuple = [
  '#f0fdf4',
  '#dcfce7',
  '#bbf7d0',
  '#86efac',
  '#4ade80',
  '#22c55e',
  '#16a34a',
  '#15803d',
  '#166534',
  '#14532d',
];

// Красный — ПОТЕРЯ в дельте относительно моей цены (рынок против меня). Опорный loss.6 #dc2626.
const loss: MantineColorsTuple = [
  '#fef2f2',
  '#fee2e2',
  '#fecaca',
  '#fca5a5',
  '#f87171',
  '#ef4444',
  '#dc2626',
  '#b91c1c',
  '#991b1b',
  '#7f1d1d',
];

// Светлый лайм выведен из языка: имя `lime` оставлено за успехом и наведением, но это зелёные тона основного цвета.
// Так цвет меняется сразу во всём портале, а не в сотне мест.
const lime: MantineColorsTuple = [
  '#eef5ea',
  '#dcebd5',
  '#b9d8b3',
  '#8cc08e',
  '#2b7a4b',
  '#2b7a4b',
  '#2b7a4b',
  '#23653e',
  '#1c5232',
  '#14261c',
];

/** Основной шрифт интерфейса: Onest — текст, заголовки и цифры (решение 2026-10-29, заменил Golos, Inter, Manrope). */
const FONT_UI = "'Onest Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/**
 * Машинные идентификаторы, которые сверяют по символам: номер сделки, ИНН, КПП, ОГРН, счёт, БИК, ключи и токены.
 * В коде — через компонент IdText и MONO_INPUT_STYLES. Цены, даты и объёмы им не набираются.
 */
const FONT_MONO = "'JetBrains Mono Variable', ui-monospace, SFMono-Regular, Menlo, monospace";

/** Поле ввода с машинным значением (API-ключ, адрес сервера): `<TextInput styles={MONO_INPUT_STYLES} />`. */
export const MONO_INPUT_STYLES = { input: { fontFamily: 'var(--mantine-font-family-monospace)' } } as const;

export const theme = createTheme({
  primaryColor: 'brand',
  // Анимации Mantine (меню, модальные окна) учитывают системную настройку «уменьшить движение».
  respectReducedMotion: true,
  primaryShade: { light: 7, dark: 6 },
  defaultRadius: 'md',
  // Углы почти прямые. Круглые значки задают radius="xl" и остаются круглыми.
  radius: { xs: '2px', sm: '3px', md: '3px', lg: '3px' },
  black: '#14261c',
  // Курсор-указатель на чекбоксах/радио/switch и native select — чтобы было видно, что кликабельно.
  cursorType: 'pointer',
  colors: { brand, lime, wheat, gain, loss },
  // Onest — одна гарнитура на весь интерфейс; заголовки отличаются жирностью, а не шрифтом.
  fontFamily: FONT_UI,
  fontFamilyMonospace: FONT_MONO,
  // Вес не выше 600 и без сжатия букв: Onest при большем весе выглядит приплюснутым.
  headings: {
    fontFamily: FONT_UI,
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '30px', lineHeight: '1.25' },
      h2: { fontSize: '24px', lineHeight: '1.3' },
      h3: { fontSize: '20px', lineHeight: '1.3' },
      h4: { fontSize: '17px', lineHeight: '1.35' },
      h5: { fontSize: '15px', lineHeight: '1.4' },
      h6: { fontSize: '14px', lineHeight: '1.4' },
    },
  },
  other: {
    // Полная высота шапки: 62 px строки и 3 px зелёной линии снизу.
    headerHeight: 65,
  },
  components: {
    // Ширина страницы «Бюллетеня» — 1240 px. Шапка, подвал и страницы с size="lg" стоят по одной ширине.
    Container: Container.extend({
      vars: (_, { size, fluid }) => ({ root: size === 'lg' && !fluid ? { '--container-size': rem(1240) } : {} }),
    }),
    // Основная кнопка — залитая зелёная с белым текстом. Вторичные кнопки задают свой variant явно.
    // Базовая высота — md (42px). Исключение — кнопки в шапке: им явно задан size="sm" (36px).
    Button: { defaultProps: { size: 'md' } },
    // Значки без заглавных букв: капс в кабинете не используется.
    Badge: Badge.extend({ styles: { root: { textTransform: 'none', fontWeight: 600 } } }),
    // Фирменный стиль полей (см. inputs.module.css). Каскадит на все Input-based компоненты
    // (TextInput/PasswordInput/Select/NativeSelect/...): высота 48, шрифт 16, рамка #cbd5e1, радиус 4.
    Input: Input.extend({
      defaultProps: { size: 'md' },
      classNames: { input: inputClasses.input },
    }),
    // Подсказки и ошибки полей объясняют форму, поэтому читаемые: 14 px и контраст не ниже AA (по умолчанию 12 px,
    // бледный серый и красный 3,28:1). Действует на все формы портала.
    InputWrapper: Input.Wrapper.extend({
      styles: {
        description: { fontSize: 'var(--mantine-font-size-sm)', color: 'var(--mantine-color-gray-7)' },
        error: { fontSize: 'var(--mantine-font-size-sm)', color: 'var(--mantine-color-loss-7)' },
      },
    }),
    // Чекбоксы/радио — зафиксированный размер sm (бокс 20×20, лейбл 14). См. design-guidelines «Размеры».
    Checkbox: { defaultProps: { size: 'sm' } },
    Radio: { defaultProps: { size: 'sm' } },
  },
});
